import React from 'react';
import { Helmet } from '@plone/volto/helpers';
import { useSiteConfig } from '../../helpers/useSiteConfig';

/**
 * Sitewide RSS feed-discovery `<link>` (returning-visitor fix, BL-0099).
 *
 * `/changes/rss` has served real entries on all three archives since
 * BL-0129 (closed 2026-09-21), but no page ever declared it: curled the home
 * page on all three frontend ports on 2026-09-27 and grepped for
 * `rss+xml` — zero matches on standards, directives and nnsa. A feed that
 * exists but is never linked from `<head>` is invisible to the three classes
 * of client that actually find feeds this way — a browser's own feed
 * discovery, a feed reader pointed at the site root rather than the exact
 * feed URL, and a crawler that checks `rel=alternate` before guessing
 * `/rss.xml` — which is consistent with the low, direct-URL-only fetch
 * volume against `/changes/rss` already measured (18 fetches in 14 days).
 *
 * Registered as an appExtra with `match: ''` (every route), the same pattern
 * as SiteTheme/NoIndexRoutes/SiteAnalytics: it has to be on EVERY page, not
 * just `/changes` or the home page, because feed discovery is a per-site
 * fact ("this whole archive has a changes feed"), not a per-document one.
 * `useSiteConfig()` with no argument is the same call SiteTheme makes for the
 * identical reason — this can render before any content object exists (a 404,
 * `/search`, `/changes` itself), and the site is still knowable from
 * RAZZLE_SITE_ID / the redux store / the route, per useSiteConfig's own
 * resolution order.
 *
 * `title` uses `site.seoName` — the same field DocumentSEO and SiteSEO use
 * for "the site's name" everywhere else in this addon (the document `<title>`
 * suffix, the site-root `<title>`) — never `site.title` (the short masthead
 * brand) or PUBLISHER_NAME (the operator, not the archive).
 */
export const feedHref = (baseUrl) => {
  const origin = (baseUrl || '').replace(/\/+$/, '');
  return origin ? `${origin}/changes/rss` : '';
};

const FeedDiscovery = () => {
  const site = useSiteConfig();
  const href = feedHref(site.baseUrl);
  if (!href) return null;
  return (
    <Helmet>
      <link
        rel="alternate"
        type="application/rss+xml"
        title={`${site.seoName} - changes`}
        href={href}
      />
    </Helmet>
  );
};

export default FeedDiscovery;
